import { Checkbox, Text } from '@fluentui/react-components';
import {
  ChatUserType,
  GuardianChatParticipantCandidatesResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyAvatarUser } from '../../Avatar';
import { Button } from '../../Button';
import { Loader } from '../../Loader';
import { ResponsiveDialog } from '../../ResponsiveDialog/ResponsiveDialog';
import { SearchInput } from '../../SearchInput';
import { TabItemChangeEvent, TabItemProps, TabsGeneric } from '../../Tabs';
import { ChatLoadingState, useChatContextOptional } from '../ChatContext';
import { ChatParticipantChip } from '../ChatParticipantChip/ChatParticipantChip';
import './ChatParticipantPickerDialog.scss';

type TabKey = 'teachers' | 'children';
type SelectionMode = 'multi' | 'single';

export interface ChatParticipantPickerDialogProps {
  open: boolean;
  candidates?: GuardianChatParticipantCandidatesResponse[];
  onConfirm: (selected: GuardianChatParticipantCandidatesResponse[]) => void;
  onCancel: () => void;
  selectionMode?: SelectionMode;
  participantsLoadingState?: ChatLoadingState;
  showTeachers?: boolean;
}

export const ChatParticipantPickerDialog: React.FC<ChatParticipantPickerDialogProps> = (props) => {
  const context = useChatContextOptional();
  const { t } = useTranslation();
  const open = props.open;
  const candidates = context?.participantCandidates ?? props.candidates ?? [];
  const participantsLoadingState =
    context?.participantsLoadingState ?? props.participantsLoadingState;
  const onConfirm = props.onConfirm;
  const onCancel = props.onCancel;
  const selectionMode = props.selectionMode ?? 'multi';
  const [activeTab, setActiveTab] = useState<TabKey>(!props.showTeachers ? 'children' : 'teachers');
  const [selectedCandidates, setSelectedCandiates] = useState<
    GuardianChatParticipantCandidatesResponse[]
  >([]);
  const [searchQuery, setSearchQuery] = useState('');

  const tabItems = useMemo(() => {
    const items: TabItemProps<TabKey>[] = [
      {
        key: 'children',
        content: t('pc:chat:participantPicker:tab_children', { defaultValue: 'Kinder' }),
        ariaLabel: t('pc:chat:participantPicker:tab_children', { defaultValue: 'Kinder' }),
      },
    ];

    if (props.showTeachers === true) {
      items.unshift({
        key: 'teachers',
        content: t('pc:chat:participantPicker:tab_teachers', { defaultValue: 'Lehrkräfte' }),
        ariaLabel: t('pc:chat:participantPicker:tab_teachers', { defaultValue: 'Lehrkräfte' }),
      });
    }
    return items;
  }, [props.showTeachers]);

  const currentList = candidates.filter(
    (c) => c.userType === (activeTab === 'teachers' ? ChatUserType.teacher : ChatUserType.ward),
  );
  const searchQueryLc = searchQuery.toLowerCase();

  const filteredList = useMemo(
    () =>
      currentList.filter(
        (candidate) =>
          searchQueryLc === '' || candidate.displayName.toLowerCase().includes(searchQueryLc),
      ),
    [currentList, searchQueryLc],
  );

  const isSelected = (candidate: GuardianChatParticipantCandidatesResponse) =>
    selectedCandidates.some((p) => p.userId === candidate.userId);

  const toggleCandidate = (candidate: GuardianChatParticipantCandidatesResponse) => {
    if (selectionMode === 'multi') {
      setSelectedCandiates((prev) =>
        prev.some((p) => p.userId === candidate.userId)
          ? // Remove previously selected
            prev.filter((p) => p.userId !== candidate.userId)
          : // Add new
            [...prev, candidate],
      );
    } else {
      // 1 to 1 chat, with one child and one teacher. Enforces only one participant of one type.
      setSelectedCandiates((prev) => [
        ...prev.filter((p) => p.userType !== candidate.userType),
        candidate,
      ]);
    }
  };

  const removeCandidate = (candidate: GuardianChatParticipantCandidatesResponse) => {
    setSelectedCandiates((prev) => prev.filter((p) => p.userId !== candidate.userId));
  };

  const handleConfirm = () => {
    onConfirm(selectedCandidates);
  };

  const handleTabChange = (event: TabItemChangeEvent<TabKey>) => {
    setActiveTab(event.key);
    setSearchQuery('');
  };

  return (
    <ResponsiveDialog
      open={open}
      header={t('pc:chat:participantPicker:header', { defaultValue: 'Teilnehmer auswählen' })}
      primaryAction={
        <Button
          appearance="primary"
          disabled={participantsLoadingState !== 'loaded' || selectedCandidates.length === 0}
          onClick={handleConfirm}
        >
          {t('pc:chat:participantPicker:confirm_button', {
            defaultValue: 'Auswählen ({{count}})',
            count: selectedCandidates.length,
          })}
        </Button>
      }
      secondaryAction={
        <Button onClick={onCancel}>
          {t('pc:chat:participantPicker:cancel_button', { defaultValue: 'Abbrechen' })}
        </Button>
      }
      className="ChatParticipantPickerDialog"
    >
      <TabsGeneric items={tabItems} onItemClick={handleTabChange} />

      <SearchInput
        appearance="filled-darker"
        className="searchInput"
        placeholder={t('pc:chat:participantPicker:search_placeholder', {
          defaultValue: 'Suchen...',
        })}
        aria-label={t('pc:chat:participantPicker:search_placeholder', {
          defaultValue: 'Suchen...',
        })}
        value={searchQuery}
        onChange={(_e, data) => setSearchQuery(data.value)}
        onClear={() => setSearchQuery('')}
        disabled={participantsLoadingState !== 'loaded'}
      />

      {(participantsLoadingState === 'loading' || participantsLoadingState === 'idle') && (
        <div className="candidateList flex items-center justify-center">
          <Loader />
        </div>
      )}
      {participantsLoadingState === 'error' && (
        <div className="candidateList flex items-center justify-center">
          <Text>
            {t('pc:chat:participant_candidates_loading_error', {
              defaultValue: 'Fehler beim Laden der Teilnehmer',
            })}
          </Text>
        </div>
      )}

      {participantsLoadingState === 'loaded' && (
        <>
          {selectedCandidates.length > 0 && (
            <div className="selectedChips">
              {selectedCandidates.map((candidate) => (
                <ChatParticipantChip
                  key={candidate.userId}
                  displayName={candidate.displayName}
                  onRemoveClick={() => removeCandidate(candidate)}
                />
              ))}
            </div>
          )}

          {filteredList.length > 0 ? (
            <div className="candidateList" role="listbox" aria-multiselectable="true">
              {filteredList.map((candidate) => {
                const displayName = candidate.displayName;
                const selected = isSelected(candidate);
                return (
                  <div
                    key={candidate.userId}
                    className="candidateItem"
                    role="option"
                    aria-selected={selected}
                    tabIndex={0}
                    onClick={() => toggleCandidate(candidate)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleCandidate(candidate);
                      }
                    }}
                  >
                    <Checkbox checked={selected} tabIndex={-1} aria-hidden="true" />
                    <LazyAvatarUser userId={candidate.userId} />
                    <span className="candidateName">{displayName}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="emptyList">
              <Text>
                {t('pc:chat:participantPicker:no_persons_found', {
                  defaultValue: 'Keine Personen gefunden',
                })}
              </Text>
            </div>
          )}
        </>
      )}
    </ResponsiveDialog>
  );
};
